import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache for supplier colours to avoid repeated API calls
let colourCache = {};
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * ColourSwatch component - displays a colour swatch
 * @param {string} hex - Hex colour value (e.g., "#FF0000")
 * @param {string} name - Colour name for fallback display
 * @param {string} size - Size: "xs", "sm", "md", "lg"
 * @param {boolean} showLabel - Show colour name next to swatch
 * @param {string} className - Additional classes
 */
export function ColourSwatch({ 
  hex, 
  name, 
  size = "sm", 
  showLabel = false,
  className = "" 
}) {
  const sizeClasses = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8"
  };

  const hasHex = hex && hex.startsWith("#");
  
  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <div 
        className={`${sizeClasses[size]} rounded border border-gray-300 shadow-sm flex-shrink-0`}
        style={{ backgroundColor: hasHex ? hex : "#f3f4f6" }}
        title={hasHex ? `${name || ""}: ${hex}` : name || "No hex value"}
      >
        {!hasHex && (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-[6px] text-gray-400">?</span>
          </div>
        )}
      </div>
      {showLabel && name && (
        <span className="text-xs text-gray-600 truncate">{name}</span>
      )}
    </div>
  );
}

/**
 * ColourSwatchWithLookup - looks up hex from supplier colour library
 * @param {string} colourName - Name of the colour
 * @param {string} supplier - Supplier name for lookup
 * @param {string} size - Size: "xs", "sm", "md", "lg"
 * @param {boolean} showLabel - Show colour name next to swatch
 */
export function ColourSwatchWithLookup({
  colourName,
  supplier,
  size = "sm",
  showLabel = true,
  className = ""
}) {
  const [hex, setHex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const lookupColour = async () => {
      if (!colourName) {
        setLoading(false);
        return;
      }

      // Check cache first
      const cacheKey = `${supplier}|${colourName}`.toLowerCase();
      const now = Date.now();
      
      if (cacheTimestamp > 0 && (now - cacheTimestamp) < CACHE_DURATION) {
        if (colourCache[cacheKey]) {
          setHex(colourCache[cacheKey]);
          setLoading(false);
          return;
        }
      }

      // If cache is stale or doesn't have colour, refresh cache
      if (now - cacheTimestamp >= CACHE_DURATION) {
        try {
          const res = await axios.get(`${API}/supplier-colours?active_only=true`);
          const newCache = {};
          (res.data.colours || []).forEach(c => {
            const key = `${c.supplier}|${c.name}`.toLowerCase();
            if (c.hex) {
              newCache[key] = c.hex;
            }
          });
          colourCache = newCache;
          cacheTimestamp = now;
          
          if (newCache[cacheKey]) {
            setHex(newCache[cacheKey]);
          }
        } catch (error) {
          console.error("Failed to load colour cache", error);
        }
      }
      
      setLoading(false);
    };

    lookupColour();
  }, [colourName, supplier]);

  if (!colourName) return null;

  return (
    <ColourSwatch 
      hex={hex}
      name={colourName}
      size={size}
      showLabel={showLabel}
      className={className}
    />
  );
}

/**
 * Hook to get supplier colours for a specific supplier
 */
export function useSupplierColours(supplier) {
  const [colours, setColours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadColours = async () => {
      if (!supplier) {
        setColours([]);
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${API}/supplier-colours`, {
          params: { supplier, active_only: true }
        });
        setColours(res.data.colours || []);
      } catch (error) {
        console.error("Failed to load supplier colours", error);
        setColours([]);
      } finally {
        setLoading(false);
      }
    };

    loadColours();
  }, [supplier]);

  return { colours, loading };
}

/**
 * Hook to get all supplier colours (for lookup)
 */
export function useAllColours() {
  const [colourMap, setColourMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadColours = async () => {
      try {
        const res = await axios.get(`${API}/supplier-colours?active_only=true`);
        const map = {};
        (res.data.colours || []).forEach(c => {
          // Key by supplier+name and just name for flexible lookup
          const supplierKey = `${c.supplier}|${c.name}`.toLowerCase();
          const nameKey = c.name.toLowerCase();
          map[supplierKey] = c;
          if (!map[nameKey]) {
            map[nameKey] = c; // First match wins for name-only lookup
          }
        });
        setColourMap(map);
      } catch (error) {
        console.error("Failed to load all colours", error);
      } finally {
        setLoading(false);
      }
    };

    loadColours();
  }, []);

  const getColour = (name, supplier = null) => {
    if (!name) return null;
    if (supplier) {
      const supplierKey = `${supplier}|${name}`.toLowerCase();
      if (colourMap[supplierKey]) return colourMap[supplierKey];
    }
    // Fallback to name-only lookup
    return colourMap[name.toLowerCase()] || null;
  };

  return { colourMap, loading, getColour };
}

export default ColourSwatch;
